/**
 * Shipping slice — Redux Toolkit state + thunks for shipping rate
 * lookups, customer order tracking, admin shipment management, and
 * admin damage-claim operations.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   availableRates: ShippingRateDTO[];
 *   selectedRate: ShippingRateDTO | null;
 *   isLoadingRates: boolean;
 *   rateError: string | null;
 *   shipments: ShipmentDTO[];
 *   damageClaims: ShippingDamageClaimDTO[];
 *   trackingData: Record<number, OrderTrackingDTO>;
 *   pagination: ShippingPaginationMeta | null;
 * }
 * ```
 *
 * `trackingData` is keyed by orderId per the ticket's Technical Notes so
 * a single Redux store can hold multiple in-flight tracking views without
 * collision.
 *
 * Per `.claude/rules/frontend-data-flow.md`:
 *
 *  - Capsules MUST NOT import this slice. Containers do.
 *  - The four admin-scoped thunks call the same shared axios instance as
 *    the customer-facing thunks; route protection is a platform concern.
 *  - Errors caught from the api-client are already `ApiError` instances
 *    (SH-AUTH/CON-PRICE precedent); we project the `.message` onto
 *    `rateError` for the rate-fetch flow (per AC) and forward verbatim
 *    via `rejectWithValue` for the other thunks.
 *
 * SH-SHIP-001.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import * as shippingApi from '@dfq/shared/api/shipping';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  CreateShipmentPayload,
  FetchCustomerShippingRatesPayload,
  FetchShippingRatesPayload,
  OrderTrackingDTO,
  ShipmentDTO,
  ShippingDamageClaimDTO,
  ShippingDamageClaimStatus,
  ShippingPaginationMeta,
  ShippingRate,
  ShippingRateDTO,
  UpdateDamageClaimStatusPayload,
} from '@dfq/shared/types/shipping';

// `ListDamageClaimsParams` lives in the api module (it carries the camelCase
// query-shape the api client maps onto snake_case query params); re-export
// from the slice so containers have one import target for thunk arguments.
import type { ListDamageClaimsParams } from '@dfq/shared/api/shipping';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export interface ShippingState {
  availableRates: ShippingRateDTO[];
  selectedRate: ShippingRateDTO | null;
  isLoadingRates: boolean;
  rateError: string | null;
  shipments: ShipmentDTO[];
  damageClaims: ShippingDamageClaimDTO[];
  /**
   * Tracking payloads keyed by orderId. Allows multiple in-flight
   * customer tracking views (e.g. a multi-order dashboard) to coexist
   * without overwriting each other.
   */
  trackingData: Record<number, OrderTrackingDTO>;
  /**
   * Pagination metadata for the most recent `fetchDamageClaims` response.
   * `null` until the first fetch resolves.
   */
  pagination: ShippingPaginationMeta | null;
  /**
   * SF-SHIP-001 — customer-facing rate-quote sub-state. Kept separate from
   * the admin-flavored `availableRates`/`selectedRate` so the storefront
   * checkout selector can drive a richer wire shape (recommendation /
   * rush / free-shipping flags) without coupling to the admin DTO.
   *
   * Optional in the type so legacy test fixtures (SH-SHIP-001) that
   * constructed `ShippingState` literals before this field existed
   * continue to type-check. Reducer cases lazily seed it from
   * `initialCustomerState` when missing — selectors return safe defaults.
   */
  customer?: CustomerShippingState;
}

/**
 * Customer-facing rate state owned by the storefront checkout flow
 * (SF-SHIP-001). Rate rows are stored in their wire shape (snake_case)
 * because the storefront capsule's adapter layer expects that shape.
 */
export interface CustomerShippingState {
  availableRates: ShippingRate[];
  selectedRate: ShippingRate | null;
  isLoadingRates: boolean;
  rateError: string | null;
}

const initialCustomerState: CustomerShippingState = {
  availableRates: [],
  selectedRate: null,
  isLoadingRates: false,
  rateError: null,
};

const initialState: ShippingState = {
  availableRates: [],
  selectedRate: null,
  isLoadingRates: false,
  rateError: null,
  shipments: [],
  damageClaims: [],
  trackingData: {},
  pagination: null,
  customer: initialCustomerState,
};

// ----------------------------------------------------------------------------
// Rejection helper — preserves an ApiError-shaped reject payload so reducer
// cases that need the platform envelope details (severity, errors map,
// error_code) can read them directly.
// ----------------------------------------------------------------------------

function rejectionFromUnknown(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) {
    return new ApiError({ message: err.message, status: 0 });
  }
  return new ApiError({ message: 'Unknown shipping API error', status: 0 });
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * `POST /api/v1/shipping/rates` — quote multi-carrier shipping options
 * for the active cart at the given destination zip.
 *
 * Per AC:
 *  - pending sets `isLoadingRates: true` and clears `rateError`.
 *  - fulfilled replaces `availableRates` and clears `isLoadingRates`.
 *  - rejected sets `rateError` from the envelope message and
 *    `isLoadingRates: false`.
 *  - If the previously selected rate's `carrier`+`serviceLevel` tuple is
 *    no longer present in the new results, the slice clears `selectedRate`
 *    so the checkout view re-prompts the customer.
 */
export const fetchShippingRates = createAsyncThunk<
  ShippingRateDTO[],
  FetchShippingRatesPayload,
  { rejectValue: ApiError }
>('shipping/fetchShippingRates', async (payload, { rejectWithValue }) => {
  try {
    return await shippingApi.fetchShippingRates(payload);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * `GET /api/v1/orders/{orderId}/tracking` — pull the carrier event feed
 * for a customer-owned order. Stored in `trackingData[orderId]` so
 * multiple lookups coexist.
 */
export const fetchOrderTracking = createAsyncThunk<
  { orderId: number; tracking: OrderTrackingDTO },
  number,
  { rejectValue: ApiError }
>('shipping/fetchOrderTracking', async (orderId, { rejectWithValue }) => {
  try {
    const tracking = await shippingApi.fetchOrderTracking(orderId);
    return { orderId, tracking };
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * `GET /api/v1/admin/orders/{orderId}/shipments` — admin shipment list
 * for a single order. Replaces `state.shipments` on every fulfilled
 * dispatch (the admin view is single-order-scoped).
 */
export const fetchShipments = createAsyncThunk<
  ShipmentDTO[],
  number,
  { rejectValue: ApiError }
>('shipping/fetchShipments', async (orderId, { rejectWithValue }) => {
  try {
    return await shippingApi.fetchOrderShipments(orderId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

export interface CreateShipmentArg {
  orderId: number;
  payload: CreateShipmentPayload;
}

/**
 * `POST /api/v1/admin/orders/{orderId}/shipments` — create a shipment +
 * generate carrier label. Appends the persisted shipment to
 * `state.shipments` on success.
 */
export const createShipment = createAsyncThunk<
  ShipmentDTO,
  CreateShipmentArg,
  { rejectValue: ApiError }
>('shipping/createShipment', async ({ orderId, payload }, { rejectWithValue }) => {
  try {
    return await shippingApi.createOrderShipment(orderId, payload);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * `GET /api/v1/admin/damage-claims` — paginated admin claim list. The
 * fulfilled payload carries the `pagination` meta alongside the rows so
 * the slice can store both in one transaction.
 */
export const fetchDamageClaims = createAsyncThunk<
  shippingApi.ListDamageClaimsResult,
  ListDamageClaimsParams | undefined,
  { rejectValue: ApiError }
>('shipping/fetchDamageClaims', async (params, { rejectWithValue }) => {
  try {
    return await shippingApi.fetchDamageClaims(params ?? {});
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

export interface UpdateDamageClaimStatusArg {
  claimId: number;
  payload: UpdateDamageClaimStatusPayload;
  /**
   * Snapshot of the claim's previous status, captured by the container at
   * dispatch time. Used by the rejected reducer to roll back the
   * optimistic update. The full claim object isn't required — only the
   * status — but containers can pass the full DTO if convenient.
   */
  previousStatus: ShippingDamageClaimStatus;
  /**
   * Optional snapshot of the previous resolution notes. The optimistic
   * update overwrites `resolutionNotes` on the matching row when the
   * payload supplies them; the rejected reducer restores this value.
   * Defaults to `null` when undefined.
   */
  previousResolutionNotes?: string | null;
}

/**
 * `PUT /api/v1/admin/damage-claims/{claimId}` — admin status update with
 * optimistic mutation. The pending case mirrors the new status (and
 * resolution notes when supplied) onto the matching row; the rejected
 * case restores the snapshot the container provided.
 */
export const updateDamageClaimStatus = createAsyncThunk<
  ShippingDamageClaimDTO,
  UpdateDamageClaimStatusArg,
  { rejectValue: ApiError }
>(
  'shipping/updateDamageClaimStatus',
  async ({ claimId, payload }, { rejectWithValue }) => {
    try {
      return await shippingApi.updateDamageClaimStatus(claimId, payload);
    } catch (err) {
      return rejectWithValue(rejectionFromUnknown(err));
    }
  },
);

// ----------------------------------------------------------------------------
// SF-SHIP-001 — customer-facing rate quote thunk
// ----------------------------------------------------------------------------

/**
 * `POST /api/v1/shipping/rates` (customer storefront variant).
 *
 * Fetches the customer-facing rate set with recommendation / rush /
 * free-shipping flags. Errors are projected onto a string `rateError`
 * field per the SF-SHIP-001 acceptance criteria — containers display
 * the message inline rather than an `ApiError` object.
 */
export const fetchCustomerShippingRates = createAsyncThunk<
  ShippingRate[],
  FetchCustomerShippingRatesPayload,
  { rejectValue: string }
>(
  'shipping/fetchCustomerShippingRates',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await shippingApi.fetchShippingRatesRequest(payload);
      return data.rates;
    } catch (err) {
      if (err instanceof ApiError) {
        return rejectWithValue(err.message);
      }
      if (err instanceof Error) {
        return rejectWithValue(err.message);
      }
      return rejectWithValue(
        'Unable to fetch shipping rates. Please try again.',
      );
    }
  },
);

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const shippingSlice = createSlice({
  name: 'shipping',
  initialState,
  reducers: {
    /**
     * Set the customer-selected rate from the available list. Stores the
     * tuple-equal entry from `availableRates` (or the supplied rate when
     * the caller wants to set a rate the slice doesn't yet know about,
     * which is unusual but legal).
     */
    selectShippingRate(state, action: PayloadAction<ShippingRateDTO | null>) {
      state.selectedRate = action.payload;
    },
    /** Clear the rate selection without touching the available-rates list. */
    clearSelectedRate(state) {
      state.selectedRate = null;
    },
    /** Reset slice to its initial shape — for logout flows. */
    resetShippingState() {
      return initialState;
    },
    // ----- SF-SHIP-001 customer-facing actions -------------------------------
    /**
     * Storefront variant — set the currently-selected customer rate. Operates
     * on the wire-shape `ShippingRate` (snake_case + flags) and lives on the
     * `customer` sub-tree so it doesn't collide with the admin `selectedRate`.
     */
    setSelectedRate(state, action: PayloadAction<ShippingRate | null>) {
      if (!state.customer) state.customer = { ...initialCustomerState };
      state.customer.selectedRate = action.payload;
    },
    /**
     * Storefront variant — clear the customer rate set entirely (used when
     * the destination zip changes, the cart line-up changes, or the
     * customer logs out of checkout).
     */
    clearShippingRates(state) {
      state.customer = { ...initialCustomerState };
    },
  },
  extraReducers(builder) {
    builder
      // -- fetchShippingRates ----------------------------------------------
      .addCase(fetchShippingRates.pending, (state) => {
        state.isLoadingRates = true;
        state.rateError = null;
      })
      .addCase(fetchShippingRates.fulfilled, (state, action) => {
        state.isLoadingRates = false;
        state.availableRates = action.payload;
        state.rateError = null;

        // If a previously-selected rate is no longer present in the new
        // results, clear it so the checkout view re-prompts. Tuple match
        // on (carrier, serviceLevel) — same rule the platform uses to
        // reconcile a stored selection against a re-quote.
        if (state.selectedRate !== null) {
          const stillOffered = action.payload.some(
            (r) =>
              r.carrier === state.selectedRate?.carrier &&
              r.serviceLevel === state.selectedRate?.serviceLevel,
          );
          if (!stillOffered) {
            state.selectedRate = null;
          }
        }
      })
      .addCase(fetchShippingRates.rejected, (state, action) => {
        state.isLoadingRates = false;
        state.rateError =
          action.payload?.message ??
          action.error.message ??
          'Failed to fetch shipping rates';
      })
      // -- fetchOrderTracking ----------------------------------------------
      .addCase(fetchOrderTracking.fulfilled, (state, action) => {
        state.trackingData[action.payload.orderId] = action.payload.tracking;
      })
      // -- fetchShipments --------------------------------------------------
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.shipments = action.payload;
      })
      // -- createShipment --------------------------------------------------
      .addCase(createShipment.fulfilled, (state, action) => {
        state.shipments.push(action.payload);
      })
      // -- fetchDamageClaims -----------------------------------------------
      .addCase(fetchDamageClaims.fulfilled, (state, action) => {
        state.damageClaims = action.payload.claims;
        state.pagination = action.payload.pagination;
      })
      // -- updateDamageClaimStatus (OPTIMISTIC) ----------------------------
      .addCase(updateDamageClaimStatus.pending, (state, action) => {
        const idx = state.damageClaims.findIndex(
          (c) => c.id === action.meta.arg.claimId,
        );
        if (idx >= 0) {
          state.damageClaims[idx] = {
            ...state.damageClaims[idx],
            status: action.meta.arg.payload.status,
            resolutionNotes:
              action.meta.arg.payload.resolution_notes ??
              state.damageClaims[idx].resolutionNotes,
          };
        }
      })
      .addCase(updateDamageClaimStatus.fulfilled, (state, action) => {
        // Replace the row with the platform's authoritative copy (which
        // includes any server-side timestamps the optimistic write
        // didn't know about).
        const idx = state.damageClaims.findIndex((c) => c.id === action.payload.id);
        if (idx >= 0) {
          state.damageClaims[idx] = action.payload;
        }
      })
      .addCase(updateDamageClaimStatus.rejected, (state, action) => {
        const idx = state.damageClaims.findIndex(
          (c) => c.id === action.meta.arg.claimId,
        );
        if (idx >= 0) {
          // `previousResolutionNotes` may be explicitly `null` (the
          // pre-mutation value was null) — distinguish that from
          // "container did not provide a snapshot" so a real null actually
          // overwrites the optimistic write rather than leaking through.
          const restoredNotes =
            'previousResolutionNotes' in action.meta.arg
              ? (action.meta.arg.previousResolutionNotes ?? null)
              : state.damageClaims[idx].resolutionNotes;
          state.damageClaims[idx] = {
            ...state.damageClaims[idx],
            status: action.meta.arg.previousStatus,
            resolutionNotes: restoredNotes,
          };
        }
      })
      // -- fetchCustomerShippingRates (SF-SHIP-001) ------------------------
      .addCase(fetchCustomerShippingRates.pending, (state) => {
        if (!state.customer) state.customer = { ...initialCustomerState };
        state.customer.isLoadingRates = true;
        state.customer.rateError = null;
        state.customer.availableRates = [];
      })
      .addCase(fetchCustomerShippingRates.fulfilled, (state, action) => {
        if (!state.customer) state.customer = { ...initialCustomerState };
        state.customer.isLoadingRates = false;
        state.customer.availableRates = action.payload;
      })
      .addCase(fetchCustomerShippingRates.rejected, (state, action) => {
        if (!state.customer) state.customer = { ...initialCustomerState };
        state.customer.isLoadingRates = false;
        state.customer.rateError =
          action.payload ?? 'Unable to fetch shipping rates. Please try again.';
        state.customer.availableRates = [];
      });
  },
});

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

/**
 * Minimal slice of root state the selectors read. Importing `RootState`
 * here would create a cycle through the root barrel.
 */
export interface ShippingRootStateSlice {
  shipping: ShippingState;
}

const selectShippingState = (state: ShippingRootStateSlice): ShippingState =>
  state.shipping;

/**
 * Memoized identity selector — returns the same array reference for the
 * same underlying state slot so components consuming `availableRates`
 * don't re-render when an unrelated field of the slice changes.
 */
export const selectAvailableRates = createSelector(
  [selectShippingState],
  (s) => s.availableRates,
);

export const selectSelectedRate = createSelector(
  [selectShippingState],
  (s) => s.selectedRate,
);

export const selectIsLoadingRates = createSelector(
  [selectShippingState],
  (s) => s.isLoadingRates,
);

/** Convenience selector — exposed for containers that want the error
 * string for an inline banner without subscribing to the whole slice. */
export const selectRateError = createSelector(
  [selectShippingState],
  (s) => s.rateError,
);

export const selectShipments = createSelector(
  [selectShippingState],
  (s) => s.shipments,
);

export const selectDamageClaims = createSelector(
  [selectShippingState],
  (s) => s.damageClaims,
);

export const selectDamageClaimsPagination = createSelector(
  [selectShippingState],
  (s) => s.pagination,
);

/**
 * Per-order tracking selector. Returns `undefined` when no tracking has
 * been fetched for the given order; containers fall back to a "no
 * tracking yet" empty state.
 */
export function selectOrderTracking(
  state: ShippingRootStateSlice,
  orderId: number,
): OrderTrackingDTO | undefined {
  return state.shipping.trackingData[orderId];
}

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export const {
  selectShippingRate,
  clearSelectedRate,
  resetShippingState,
  setSelectedRate,
  clearShippingRates,
} = shippingSlice.actions;

// SF-SHIP-001 — customer-facing selectors. They read from the dedicated
// `customer` sub-tree so they don't trip on the admin `availableRates` /
// `selectedRate` slots above. Each selector falls back to the initial
// customer-state default when the sub-tree is absent (legacy state).
export const selectCustomerAvailableRates = createSelector(
  [selectShippingState],
  (s) => s.customer?.availableRates ?? initialCustomerState.availableRates,
);

export const selectCustomerSelectedRate = createSelector(
  [selectShippingState],
  (s) => s.customer?.selectedRate ?? initialCustomerState.selectedRate,
);

export const selectCustomerIsLoadingRates = createSelector(
  [selectShippingState],
  (s) => s.customer?.isLoadingRates ?? initialCustomerState.isLoadingRates,
);

export const selectCustomerRateError = createSelector(
  [selectShippingState],
  (s) => s.customer?.rateError ?? initialCustomerState.rateError,
);

export { shippingSlice };
export const shippingReducer = shippingSlice.reducer;
export default shippingReducer;

// Re-export the api-module's pagination/list params type so containers can
// `import { ShippingListDamageClaimsParams } from '@dfq/shared/stores/shippingSlice'`
// without crossing into the api module directly.
export type { ListDamageClaimsParams as ShippingListDamageClaimsParams };
